import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuCornerDownLeft, LuMessageSquareText } from "react-icons/lu";
import Avatar from "../../../common/Avatar";
import Dialog from "../../../common/Dialog";
import { notify } from "../../../common/Toast";
import { addActionLog, fetchRequestLogs } from "../../../redux/action/requestsAction/RequestsAction";
import Loader from "../../../common/Loader";

export default function RequestsLogs({ requestId }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { logsBySrequest, logsLoading } = useSelector((state) => state.request);

  const [open, setOpen] = useState(false);
  const [newComment, setNewComment] = useState("");

  const logs = logsBySrequest[requestId] || [];

  useEffect(() => {
    if (open) dispatch(fetchRequestLogs(requestId));
  }, [open, dispatch, requestId]);

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      notify.error("Comment Required", "Please enter a comment before submitting.");
      return;
    }
    const res = await dispatch(addActionLog(requestId, user?.email, "Comment", newComment));
    if (res.success) {
      setNewComment("");
      dispatch(fetchRequestLogs(requestId));
    } else {
      notify.error("Failed to add comment", res.error);
    }
  };

  const footer = (
    <div className="flex w-full items-center gap-1.5">
      <input
        value={newComment}
        onChange={(e) => setNewComment(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
        placeholder="Add a comment…"
        className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
      />
      <button
        onClick={handleAddComment}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cyan-600 text-white transition hover:bg-cyan-700"
        title="Send"
      >
        <LuCornerDownLeft size={14} />
      </button>
    </div>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-cyan-600 text-white shadow-sm transition hover:bg-cyan-700"
        title="View activity & comments"
      >
        <LuMessageSquareText size={16} />
      </button>

      {/* Rendered via Dialog (portal) instead of an absolutely-positioned
          panel — the old version got clipped whenever a parent card had
          overflow-hidden, which every supplier card does. */}
      <Dialog open={open} onClose={() => setOpen(false)} title="Activity & Comments" footer={footer} widthClass="sm:max-w-lg">
        {logsLoading && <p className="py-6 text-center text-[13px] text-slate-400"><Loader fullScreen text="Loading logs ..." /></p>}

        {!logsLoading && (
          <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
            {logs.length === 0 && <p className="py-6 text-center text-[13px] text-slate-400">No activity yet.</p>}
            {logs.map((log, i) => (
              <div key={i} className="flex gap-2.5 border-b border-slate-50 pb-3 last:border-0 last:pb-0">
                <Avatar name={log.by} size={30} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-[12.5px] font-medium text-slate-700">{log.by}</span>
                    <span className="shrink-0 text-[11px] text-slate-400">
                      {new Date(log.date).toLocaleDateString()}{" "}
                      {new Date(log.date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400">{log.type}</p>
                  {log.content && <p className="mt-0.5 text-[13px] text-slate-700">{log.content}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Dialog>
    </>
  );
}